import { Pipe, PipeTransform } from '@angular/core';
import { translate } from '@jsverse/transloco';
import { DateTime } from 'luxon';

@Pipe({
  name: 'currentDate',
  standalone: true,
  pure: false,
})
export class CurrentDatePipe implements PipeTransform {
  // The pipe is impure because the relative "Today/Tomorrow/Yesterday" prefix
  // depends on the current local day, which rolls over at midnight with no
  // change to the inputs. To keep that correctness without paying the full
  // Luxon-parse + Intl-format + translate cost on every change-detection tick
  // (this pipe is bound per-item in long feed/chat lists), memoize the result
  // and recompute only when an input — or the current local day — actually
  // changes. Each binding gets its own pipe instance, so the cache is per-usage.
  private lastDate: string | Date | null | undefined;
  private lastLang?: string;
  private lastDayKey?: string;
  private lastIncludeYear?: boolean;
  private lastResult?: string;

  transform(date: string | Date | null | undefined, activeLang = 'en', includeYear = false): string {
    const dayKey = this.localDayKey();

    if (
      this.lastResult !== undefined &&
      date === this.lastDate &&
      activeLang === this.lastLang &&
      dayKey === this.lastDayKey &&
      includeYear === this.lastIncludeYear
    ) {
      return this.lastResult;
    }

    const result = this.compute(date, activeLang, includeYear);

    this.lastDate = date;
    this.lastLang = activeLang;
    this.lastDayKey = dayKey;
    this.lastIncludeYear = includeYear;
    this.lastResult = result;

    return result;
  }

  // Cheap local year-month-day stamp (no Luxon DateTime allocation) used only
  // to detect a midnight rollover between change-detection ticks.
  private localDayKey(): string {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
  }

  private compute(date: string | Date | null | undefined, activeLang: string, includeYear: boolean): string {
    const currentDate = this.createDateTime('local', activeLang).startOf('day');
    if (!date) return currentDate.toISO() as string;

    const comparisonDate = this.toDateTime(date, 'local', activeLang).startOf('day');

    const day = this.getDayDescription(currentDate, comparisonDate, activeLang);
    // A relative "Today/Yesterday/Tomorrow" date is within a day of now, so the year is
    // redundant — drop it even when includeYear is requested (e.g. "Today, Jul 22", not
    // "Today, Jul 22, 2026"). Absolute dates still honour includeYear.
    const formattedDate = this.getFormattedDate(comparisonDate, activeLang, includeYear && !day);

    return day ? `${day}${formattedDate}` : formattedDate;
  }

  private createDateTime(timeZone: string, activeLang?: string): DateTime {
    return DateTime.local({ zone: timeZone, locale: activeLang || '' });
  }

  private toDateTime(date: string | Date, timeZone: string, activeLang?: string): DateTime {
    const opts = { zone: timeZone, locale: activeLang || '' };
    return typeof date === 'string' ? DateTime.fromISO(date, opts) : DateTime.fromJSDate(date, opts);
  }

  private getDayDescription(currentDate: DateTime, comparisonDate: DateTime, activeLang?: string): string | undefined {
    const dayKeys: { [key: string]: DateTime } = {
      'Today, ': currentDate,
      'Tomorrow, ': currentDate.plus({ days: 1 }),
      'Yesterday, ': currentDate.minus({ days: 1 }),
    };

    for (const [key, dateTime] of Object.entries(dayKeys)) {
      if (comparisonDate.hasSame(dateTime, 'day')) {
        return translate(key, {}, activeLang);
      }
    }

    return;
  }

  private getFormattedDate(dateTime: DateTime, activeLang: string, includeYear: boolean): string {
    // Locale-aware date — Intl chooses the natural ordering for each language
    // (e.g. en: "May 25", sv: "25 maj", is: "25. maí"); with includeYear the year
    // is appended in that locale's order too (en: "May 25, 2026", sv: "25 maj 2026").
    return dateTime
      .setLocale(activeLang)
      .toLocaleString(
        includeYear ? { year: 'numeric', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' },
      );
  }
}
